import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import CometChatModal from 'components/base/Modal/CometChatModal'
import type { RootState } from 'redux/types'
import style from './style.module.scss'

const MODAL_ID = 'ConversationSearchEnabled'
const SUPPORT_TICKET_URL = 'https://help.cometchat.com/hc/en-us/requests/new'
const PRICING_URL = 'https://www.cometchat.com/pricing'

/**
 * Confirmation shown after Conversation & Advanced Search is enabled.
 *
 * Rendered by `layouts/Main` and driven by `settings.visibleModalID`; the `apps` saga sets that
 * id once the setting saves.
 */
const ConversationSearchEnabled: React.FC = () => {
  const settings = useSelector((state: RootState) => state.settings)
  const dispatch = useDispatch()

  const { visibleModalID } = settings as { visibleModalID?: string }

  const close = () =>
    dispatch({
      type: 'settings/CHANGE_SETTING',
      payload: { setting: 'visibleModalID', value: '' },
    })

  return (
    <CometChatModal
      open={visibleModalID === MODAL_ID}
      onCancel={close}
      onOk={close}
      title="Conversation and Advanced Search Enabled"
      description="Advanced Search has been successfully enabled. All future messages will be indexed automatically. Please note that it may take up to 10 minutes for indexing to begin."
      okText="Got it"
      hideCancel
      headerAlign="center"
      featuredIcon={<span className={style['cc-search-enabled__icon']} aria-hidden="true" />}
      maskClosable={false}
      showClose={false}
    >
      <ul className={style['cc-search-enabled__notes']}>
        <li>
          To index historical messages, please create a{' '}
          {/* Real anchors, not `span[role=button]` — the legacy version fired its handler on ANY
              key, so tabbing over these opened a new tab. */}
          <a href={SUPPORT_TICKET_URL} target="_blank" rel="noopener noreferrer">
            support ticket
          </a>
          .
        </li>
        <li>
          This feature may generate additional usage charges. Refer to the{' '}
          <a href={PRICING_URL} target="_blank" rel="noopener noreferrer">
            pricing page
          </a>{' '}
          for details.
        </li>
        <li>You can enable/disable this setting anytime.</li>
      </ul>
    </CometChatModal>
  )
}

export default ConversationSearchEnabled
